import { useState } from "react";
import { motion } from "framer-motion";
import { AppHeader } from "@/components/sartn/AppHeader";
import { useContactStore } from "@/stores/contactStore";
import { useLanguageStore } from "@/stores/languageStore";
import { cn } from "@/lib/cn";
import { CATEGORY_CONFIG } from "@/components/sartn/CategoryBadge";
import type { ContactCategory } from "@/components/sartn/CategoryBadge";

const CATEGORIES: ContactCategory[] = ["family", "friend", "business", "vip", "other"];

const PAGE_ENTER = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25 },
};

interface FormErrors {
  firstName?: string;
}

export default function ContactAdd() {
  const { createContact, isLoading } = useContactStore();
  const language = useLanguageStore((state) => state.language);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [category, setCategory] = useState<ContactCategory>("other");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});

  function navigateBack() {
    window.location.hash = "/contacts";
  }

  function validateForm(): boolean {
    const nextErrors: FormErrors = {};
    const trimmedFirst = firstName.trim();

    if (!trimmedFirst) {
      nextErrors.firstName = "First name is required";
    } else if (trimmedFirst.length < 2) {
      nextErrors.firstName = "First name must be at least 2 characters";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await createContact({
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        phone: phone.trim() || undefined,
        category,
        notes: notes.trim() || undefined,
      });
      navigateBack();
    } catch {
      // error already set in store
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="Add Contact" onBack={navigateBack} />

      <motion.main
        initial={PAGE_ENTER.initial}
        animate={PAGE_ENTER.animate}
        transition={PAGE_ENTER.transition}
        className="flex-1 px-4 pb-20 pt-4"
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <FormField
            label="First Name"
            required
            error={errors.firstName}
          >
            <input
              type="text"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                if (errors.firstName) setErrors({});
              }}
              placeholder="Enter first name"
              className={cn(
                "w-full rounded-[var(--radius-md)] bg-[var(--color-surface)]",
                "px-4 py-3 text-sm text-[var(--color-text)]",
                "placeholder:text-[var(--color-text-muted)]",
                "outline-none transition-colors focus:ring-1 focus:ring-[var(--color-primary)]",
                errors.firstName
                  ? "border border-[var(--color-danger)]"
                  : "shadow-sm",
              )}
            />
          </FormField>

          <FormField label="Last Name">
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter last name"
              className="w-full rounded-[var(--radius-md)] bg-[var(--color-surface)]
                         px-4 py-3 text-sm text-[var(--color-text)] shadow-sm
                         placeholder:text-[var(--color-text-muted)]
                         outline-none transition-colors focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </FormField>

          <FormField label="Phone">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+251 9XX XXX XXXX"
              className="w-full rounded-[var(--radius-md)] bg-[var(--color-surface)]
                         px-4 py-3 text-sm text-[var(--color-text)] shadow-sm
                         placeholder:text-[var(--color-text-muted)]
                         outline-none transition-colors focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </FormField>

          <FormField label="Category">
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map((categoryValue) => {
                const config = CATEGORY_CONFIG[categoryValue];
                const Icon = config.icon;
                const isSelected = category === categoryValue;

                return (
                  <button
                    key={categoryValue}
                    type="button"
                    onClick={() => setCategory(categoryValue)}
                    className={cn(
                      "flex min-h-[64px] flex-col items-center justify-center gap-1.5 rounded-2xl border px-2 py-2 text-center transition-colors active:scale-[0.98]",
                      isSelected
                        ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                        : "border-[var(--color-border)] bg-[var(--color-surface)]",
                    )}
                    aria-pressed={isSelected}
                  >
                    <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-xl", config.bgClass, config.textClass)}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-[11px] font-semibold leading-tight text-[var(--color-text)]">
                      {language === "am" ? config.labelAm : config.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </FormField>

          <FormField label="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this contact..."
              rows={3}
              className="w-full resize-none rounded-[var(--radius-md)] bg-[var(--color-surface)]
                         px-4 py-3 text-sm text-[var(--color-text)] shadow-sm
                         placeholder:text-[var(--color-text-muted)]
                         outline-none transition-colors focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </FormField>

          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              "mt-2 rounded-[var(--radius-md)] py-3.5 text-sm font-semibold text-white",
              "transition-colors active:scale-[0.98]",
              isLoading
                ? "cursor-not-allowed bg-[var(--color-primary)]/60"
                : "bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)]",
            )}
          >
            {isLoading ? "Adding..." : "Add Contact"}
          </button>
        </form>
      </motion.main>
    </div>
  );
}

function FormField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-[var(--color-text)]">
        {label}
        {required && <span className="ml-0.5 text-[var(--color-danger)]">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-xs text-[var(--color-danger)]">{error}</p>
      )}
    </div>
  );
}
